import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Row, Space } from 'antd';
import moment from 'moment';
import {
  ContentWrapper,
  StyldDivider,
  StyledImg,
  Title,
  Description,
  DateTitle,
  DateDescription,
  ButtonContainer,
  CustomDatePickerContainer,
  CustomDatePickerLabel,
  CustomDatePickerInput
} from './styled';

import img_calendar from '../../../../assets/img_calendar.png';
import downloaded_img from '../../../../assets/scan_history_downloaded.png';
import downloading_img from '../../../../assets/downloading_scan_history.png';
import DownloadModal from './downloadModal.jsx';

import {
  downloadedText,
  downloadingText,
  descDownloadedText,
  descDownloadingText,
  ghostBtnDownloadedText,
  ghostBtnDownloadingText,
  primaryBtnDownloadedText,
  primaryBtnDownloadingText,
  percentageCompleted,
  percentagePartiallyCompleted
} from './constants.js';

const HistoryOnDemand = ({
  getODNSHistoryCsvDownload,
  csvData = null,
  resetODNSHistoryCsvDownload,
  hasError = false
}) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [csvDownloaded, setCsvDownloaded] = useState(false);

  const maxDifference = 2; // Maximum difference allowed from the current date
  const maxPastDays = 120; // Maximum past days allowed

  const disableFromDate = (current) => {
    return (
      current &&
      (current.isAfter(moment()) ||
        current.isBefore(moment().subtract(maxPastDays, 'days'), 'day'))
    );
  };

  const disableToDate = (current) => {
    if (fromDate) {
      return (
        current.isAfter(moment()) ||
        current.isBefore(moment(fromDate)) ||
        current.isAfter(moment(fromDate).add(maxDifference, 'days'), 'day')
      );
    }
    return true;
  };

  const dateFormat = 'DD/MM/YYYY';

  // Function to handle the "Proceed to download" button click
  const handleDownloadButtonClick = () => {
    setModalVisible(true);
    getODNSHistoryCsvDownload({
      fromDate,
      toDate
    });
  };

  useEffect(() => {
    if (csvData !== null && csvDownloaded === false) {
      setCsvDownloaded(true);
    }
  }, [csvData, csvDownloaded]);

  useEffect(() => {
    if (!csvData && hasError) {
      stopDownload();
    }
  }, [hasError]);

  const stopDownload = () => {
    setFromDate(null);
    setToDate(null);
    setModalVisible(false);
    resetODNSHistoryCsvDownload();
    setCsvDownloaded(false);
  };

  const onChangeFromDate = (date) => {
    setFromDate(date);
    const to = moment(date).add(maxDifference, 'days');
    const current = moment();

    if (to.isBefore(current)) {
      setToDate(to);
    } else {
      setToDate(current);
    }
  };

  return (
    <ContentWrapper>
      <Row gutter={[48, 8]}>
        <Col span={5} offset={4}>
          <StyledImg src={img_calendar} />
        </Col>
        <Col
          span={8}
          justify="space-between"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column'
          }}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Title>History of Past Scans</Title>
              <Description>
                Date range selection is limited to a maximum of 3 days
              </Description>
            </Col>
          </Row>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <DateTitle>Date Range</DateTitle>
              <DateDescription>
                limit downloads to the following date range
              </DateDescription>
              <Space size={24}>
                <CustomDatePickerContainer>
                  <CustomDatePickerLabel>From Date</CustomDatePickerLabel>
                  <CustomDatePickerInput
                    placeholder=""
                    disabledDate={disableFromDate}
                    value={fromDate}
                    onChange={onChangeFromDate}
                    format={dateFormat}
                  />
                </CustomDatePickerContainer>
                <CustomDatePickerContainer style={{ width: '173.5px' }}>
                  <CustomDatePickerLabel>To Date</CustomDatePickerLabel>
                  <CustomDatePickerInput
                    placeholder=""
                    disabledDate={disableToDate}
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                    format={dateFormat}
                    disabled={!fromDate}
                    style={{ paddingLeft: '60px' }}
                  />
                </CustomDatePickerContainer>
              </Space>
            </Col>
          </Row>
          <Row gutter={[0, 16]}>
            <ButtonContainer className="on-demand-screening-button-css">
              <Button
                onClick={() => {
                  if (fromDate || toDate) {
                    setFromDate(null);
                    setToDate(null);
                  }
                }}
                disabled={!fromDate && !toDate}>
                Reset
              </Button>
              <Button
                type="primary"
                onClick={handleDownloadButtonClick}
                style={{ width: '148px', fontSize: '12px' }}
                disabled={!fromDate || !toDate}>
                Proceed to Download
              </Button>
            </ButtonContainer>
          </Row>
        </Col>
      </Row>
      <StyldDivider />
      {/* Render the DownloadModal component */}
      <DownloadModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        img={csvDownloaded ? downloaded_img : downloading_img}
        contentTitle={csvDownloaded ? downloadedText : downloadingText}
        contentDescription={
          csvDownloaded ? descDownloadedText : descDownloadingText
        }
        ghostBtnText={
          csvDownloaded ? ghostBtnDownloadedText : ghostBtnDownloadingText
        }
        primaryBtnText={
          csvDownloaded ? primaryBtnDownloadedText : primaryBtnDownloadingText
        }
        percentageCompletion={
          csvDownloaded ? percentageCompleted : percentagePartiallyCompleted
        }
        getODNSHistoryCsvDownload={getODNSHistoryCsvDownload}
        stopDownload={stopDownload}
        csvData={csvData}
      />
    </ContentWrapper>
  );
};

HistoryOnDemand.propTypes = {
  getODNSHistoryCsvDownload: PropTypes.func.isRequired,
  csvData: PropTypes.object,
  resetODNSHistoryCsvDownload: PropTypes.func.isRequired,
  hasError: PropTypes.bool
};

export default HistoryOnDemand;
